
import type { Currency, FileResponse } from 'src/services/utils/utils.types';

import type { TenantType } from 'src/types/enums';

import { ICompanyProfile } from 'src/services/connections/connections.types';
import { PageOptions, SortConfig } from '../api.types';
import { DocumentFile, DocumentType } from '../files/file.types';
import type { AttachedThread } from '../threads/threads.types';
import type { Account, Organization } from '../auth/auth.types';
import { ComplianceRequirementType } from '../shared/shared.types';
import type { IJob, PeriodType, IJobQuestion, EmploymentType, Skill as ISkill, FeeType, PaymentFrequency } from '../jobs/jobs.types';
import type {
  IProfileLink,
  IWorkExperience,
  CandidateProfile,
  IEducationHistory,
  ISalaryExpectation,
  IPreferredLocation,
  CandidateProfileData,
  CandidateProfileSectionType,
} from '../candidates/candidates.types';
import { Contact } from '../contacts/contacts.types';
import { UpdateCustomFieldValueDto } from '../custom-fields/custom-field.types';

export interface RejectionContext {
  reason: RejectionReason;
  feedback?: string;
  offer?: string;
}

export type PlacementDocument = {
  id: string;
  document: DocumentFile;
  created_at: Date;
}

export interface CreatePlacmentDocumentDto {
  file_id: string;
  name: string;
  type: DocumentType;
}

export enum SubmissionVisibilityStatus {
  HIDDEN = 'hidden',
  SHARED = 'shared',
}

export type ComplianceSubmission = {
  id: string;
  question: IJobQuestion;
  answer: {
    text?: string;
  }
  visibility_status: SubmissionVisibilityStatus;
}

export type SubmissionData = {
  question_id: string;
  submission_data: {
    text: string;
  };
}

export enum ProfitCalculationType {
  MARGIN = 'margin',
  MARKUP = 'markup',
}

export type ApplyIntent = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  country_code: string;
  created_at: Date;
  status: string;
  resume: FileResponse;
  cover_letter: FileResponse;
}

export type JobApplication = {
  id: string;
  stage: ApplicationStage;
  manager_type: TenantType;
  created_at: string;
  updated_at: string;
  rating: number;
  rejection_context?: RejectionContext;
  withdrawal_context?: WithdrawalContext;
  recruiter?: {
    company_profile: { id: string; name: string; logo: { public_path: string } };
  };
  client?: {
    company_profile: { id: string; name: string; logo: { public_path: string } };
  };
  candidate: Pick<Account, 'id'>;
  job: Pick<IJob, 'id' | 'title' | 'employment_type' | 'verification_status' | 'client_company_name'>;
  requested_stage: any; // stage
  manager_candidate_thread: AttachedThread;
  manager_client_thread: AttachedThread;
  internal_thread: AttachedThread;
  manager: {
    id: string,
    type: TenantType
  };
  sent_to_client_at: Date | null;
  open_status?: OpenStatus;
  status: ApplicationStatus;
  representation_status: RepresentationStatus;
  interview_phase?: number;
  org_candidate?: {
    id: string;
  }
  attached_profile: Pick<CandidateProfile, 'id' | 'profile_image' | 'email' | 'position_title' | 'updated_at' | 'completion_percentage' | 'phone_number' | 'country_code'> & {
    first_name: string;
    last_name: string;
  };

  apply_source?: {
    id: string;
    source: string;
  }
};

export enum RepresentationStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

export enum WithdrawalReason {
  APPLICATION_CONFLICT = 'application_conflict',
  CANDIDATE_ACTION = 'candidate_action',
}

export enum OpenStatus {
  OPENED = 'opened',
  NEW = 'new'
}

export interface WithdrawalContext {
  reason: WithdrawalReason;
}

export enum RejectionReason {
  OFFER_ACCEPTED = 'offer_accepted',
  OFFER_DECLINED = 'offer_declined',
  REJECTED = 'rejected',
  CONNECTION_UNLINK = 'connection_unlink',

  NOT_GOOD_FIT = 'not_good_fit',
  NOT_ENOUGH_EXPERIENCE = 'not_enough_experience',
  NOT_ENOUGH_QUALIFICATIONS = 'not_enough_qualifications',
  SKILL_MISMATCH = 'skill_mismatch',
  SALARY_EXPECTATIONS_MISMATCH = 'salary_expectations_mismatch',
  AVAILABILITY_ISSUES = 'availability_issues',
  LOCATION_ISSUES = 'location_issues',
  ROLE_FILLED = 'role_filled',
  ROLE_CLOSED = 'role_closed',
  OTHER = 'other',
}

export enum ApplicationStatus {
  ACTIVE = 'active',
  ARCHIVED = 'archived',
}

export enum ApplicationStage {
  INVITED = 'invited',
  INTERESTED = 'interested', // candidate only
  NO_APPLICATIONS = 'no_applications', // org only
  APPLIED = 'applied', // cand - recr
  INTERNAL_INTERVIEW = 'internal_interview', // candidate will see screening
  SCREENING = 'screening', // candidate will see screening
  READY_TO_SHORTLIST = 'ready_to_shortlist', // candidate will see screening
  SHORTLISTED = 'shortlisted', // Client On
  READY_TO_INTERVIEW = 'ready_to_interview',
  INTERVIEW = 'interview',
  REFERENCE_CHECK = 'reference_check',
  READY_TO_OFFER = 'ready_to_offer', // No candidate notification
  OFFER = 'offer',
  OFFER_ACCEPTED = 'offer_accepted',
  TO_BE_REJECTED = 'to_be_rejected',
  REJECTED = 'rejected',
  WITHDRAWN = 'withdrawn',
}

export type GetApplicationsQuery = {
  params: PageOptions & { search_query?: string, representation_status?: RepresentationStatus } & Partial<SortConfig>;
  jobId?: string;
};

export type StageChangeRequest = {
  id: string;
  target_stage: ApplicationStage;
  status: StageChangeRequestStatus;
  rejection_context: RejectionContext;
  created_at: string;
  updated_at: string;
  application: Pick<JobApplication, 'id'> & {
    attached_profile: Pick<CandidateProfile, 'id' | 'profile_image' | 'first_name' | 'last_name'>;
  };
};

export enum StageChangeRequestAction {
  Accept = 'accept',
  Defer = 'defer',
}

export enum StageChangeRequestStatus {
  PENDING = 'pending',
  COMPLETED = 'completed',
}

export enum OfferFlowType {
  STANDARD = 'standard',
  EXTERNAL = 'external',
}

export interface CreateOfferDto {
  id?: string;
  position_title: string;
  employer_name: string;
  description?: string;
  start_date: string;
  end_date: string | null;
  employment_type: EmploymentType;
  entitlements_inclusive: boolean;
  rate_period: PeriodType;
  pay_rate: string;
  notes?: string;
  required_documents?: DocumentType[];
  other_document_type?: string;
  submit_behaviour: 'save_as_draft' | 'submit';
};

export type Offer = {
  id: string;
  application: JobApplication;
  position_title: string;
  start_date: string;
  end_date: string | null;
  description?: string;
  employer_name: string;
  employment_type: EmploymentType;
  entitlements_inclusive: boolean;
  rate_period: PeriodType;
  pay_rate: string;
  status: OfferStatus;
  compliance_requirements?: {
    item_type: any;
    requirement_type: ComplianceRequirementType;
    id: string;
    custom_type_name?: string;
  }[];
  compliance_requirements_notes?: string;
  notes?: string;
  flow_type: OfferFlowType;
  candidate_name: string;
};

export enum OfferStatus {
  DRAFT = 'draft',
  IN_REVIEW = 'in_review',
  SENT = 'sent',
  CHANGES_REQUESTED = 'changes_requested',
  ACCEPTED = 'accepted',
  DECLINED = 'declined',
  RESCINDED = 'rescinded',
}

export enum MatchStatus {
  MATCH = 'match',
  NO_MATCH = 'no_match',
  NOT_ENOUGH_DATA = 'not_enough_data'
}

export enum ApplicationInsightType {
  JOB_TYPE = 'job_type',
  SALARY = 'salary',
  WORK_STYLE = 'work_style',
}

export enum OfferAction {
  Accept = 'accept',
  Decline = 'decline',
}

export type Placement = {
  id: string;
  application: JobApplication;
  job?: Pick<IJob, 'id' | 'title' | 'description' | 'excerpt'>;
  org_candidate: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  };
  placed_at: Date;

  candidate_first_name: string;
  candidate_last_name: string;
  candidate_email: string;

  job_title: string;
  employment_type: EmploymentType;

  employer_company: ICompanyProfile | null;
  employer_company_name?: string;

  recruiter_company: ICompanyProfile | null;
  recruiter_company_name?: string;
  recruiter_first_name?: string;
  recruiter_last_name?: string;

  duration_days?: number;
  billable_days?: number;
  start_date?: Date;
  end_date?: Date;

  base_salary?: number;
  total_remuneration?: number;
  pay_rate?: PeriodType;
  bill_rate?: number;
  margin?: number;
  total_margin?: number;

  hours_per_day?: number;
  additional_entitlements?: number;
  placement_fee_value?: string;
  placement_fee_type?: FeeType;
  is_prorated_fee?: boolean;
  proration_percentage?: number;
  on_costs?: number;
  profit_calculation_type?: ProfitCalculationType;

  currency?: Currency;

  billing_contact?: Contact | null;
  billing_contact_first_name?: string;
  billing_contact_last_name?: string;
  billing_contact_email?: string;
  billing_invoice_number?: string;
  billing_po_number?: string;
  payment_terms?: string;
  payment_frequency?: PaymentFrequency;

  send_invoice_on?: string;

  offer_accepted_at?: Date;
  contract_signed_at?: Date;
  contract_sent_at?: Date;

  offer_information?: string;
};

export interface CreatePlacementDto {
  job_application_id: string;
  candidate_first_name: string;
  candidate_last_name: string;
  candidate_email: string;
  job_title: string;
  employment_type: EmploymentType;

  related_offer_id?: string;
  duration_days?: string;
  billable_days?: string;
  start_date?: Date;
  end_date?: Date;
  base_salary?: number; // float
  total_remuneration?: number; // float
  pay_rate?: PeriodType;
  bill_rate?: number; // float
  margin?: number; // float
  total_margin?: number; // float

  profit_calculation_type?: ProfitCalculationType;
  hours_per_day?: number; // float
  additional_entitlements?: number; // float
  currency_code?: string;
  placement_fee_value?: number;
  placement_fee_type?: FeeType;
  is_prorated_fee?: boolean;
  proration_percentage?: number;
  on_costs?: number;

  billing_contact_id?: string;
  billing_contact_first_name?: string;
  billing_contact_last_name?: string;
  billing_contact_email?: string;
  billing_invoice_number?: string;
  billing_po_number?: string;
  payment_terms?: string;
  payment_frequency?: PeriodType;
  send_invoice_on?: string;

  employer_company_id?: string;
  employer_company_name?: string;

  recruiter_company_id?: string;
  recruiter_company_name?: string;
  recruiter_first_name?: string;
  recruiter_last_name?: string;

  offer_accepted_at?: Date;
  contract_signed_at?: Date;
  contract_sent_at?: Date;
  offer_information?: string;

  custom_fields?: UpdateCustomFieldValueDto;
}

export type ApplicationInsight = {
  id: string;
  match_status: MatchStatus;
  type: ApplicationInsightType;
  created_at: Date;
};

export enum ResourceType {
  Document = 'document',
  Reference = 'reference',
  ProfileInformation = 'profile_information',
}

export type InitiateResourceRequestProps = {
  applicationId: string;
  data: ResourceRequestData;
};

export type ResourceRequestData = {
  request_type: ResourceType;
  notes: string;
  documents?: { type: string }[];
  profile_sections?: { type: string }[];
};

export type ActionOfferRequestProps = {
  offerId: string;
  action: OfferAction;
  documents?: { type: string; profile_document_id: string }[];
};

export type LogOfferActionRequestProps = {
  offerId: string;
  action: OfferAction;
  documents?: { type: string; file_id: string }[];
};

export type NotifyOfferCandidateProps = {
  offerId: string;
  subject: string;
  content: string;
};

export type ApplicationResourceRequest = {
  id: string;

  status: ResourceRequestStatus;

  type: ResourceType;

  items: ResourceItem[];

  created_at: Date;

  notes: string;
};

export enum ResourceRequestStatus {
  PENDING = 'pending',
  COMPLETED = 'completed',
  REJECTED = 'rejected',
}

export type ResourceItem = {
  id: string;
  item_type: string | CandidateProfileSectionType;
};

export type ActionResourceRequestProps = {
  requestId: string;
  data: {
    action: ResourceRequestAction;
    documents?: { profile_document_id: string; type: DocumentType }[];
    summary?: {
      content: string;
    };
    job_types?: CandidateProfileData[];
    work_styles?: CandidateProfileData[];
    education_level?: CandidateProfileData;
    work_rights?: CandidateProfileData[];
    salary_expectations?: ISalaryExpectation[];
    preferred_locations?: IPreferredLocation[];
    work_experiences?: IWorkExperience[];
    notice_period?: CandidateProfileData;
    skills?: ISkill[];
    education_history?: IEducationHistory[];
    links?: IProfileLink[];
  };
};

export type ActionResourceRequestData = ActionResourceRequestProps['data'];

export enum ResourceRequestAction {
  ACCEPT = 'accept',
  REJECT = 'reject',
}
